import React, { useCallback, useEffect, useState } from "react";
import styled from "styled-components";
import toast from "react-hot-toast";

import Modal from "../../../../components/modal/Modal";
import Toggle from "../../../../components/form-item/Toggle";
import IconButton from "../../../../components/button/IconButton";
import ToolTip from "../../../../components/tooltip/ToolTip";
import DateInput from "../../../../components/calendar/DateInput";
import { useCopyToClipboard } from "../../../../hooks/useCopyToClipboard";
import {
  useCreateShareLinkMutation,
  useDeleteShareLinkMutation,
  useLazyGetShareLinkQuery,
} from "../../../../app/services/projectMessages";

/** 共有対象（ファイル or フォルダ） */
export interface ShareTarget {
  type: "file" | "folder";
  id: string;
  name: string;
  /** 対象が属するメッセージID（共有バッジのローカル反映用） */
  messageId?: string;
}

export interface ShareLinkModalProps {
  projectId: string;
  target: ShareTarget;
  /** 有効期限の初期値（YYYY-MM-DD。プロジェクトの開発完了日） */
  defaultExpiryDate?: string | null;
  onClose: () => void;
  /** 共有ON/OFFが変わったときの通知（バッジのローカル反映用） */
  // eslint-disable-next-line no-unused-vars
  onShareStateChanged?: (target: ShareTarget, isShared: boolean) => void;
}

// MCP（マイページ）の URL 表示と同デザイン
const URLText = styled.div`
  flex: 1;
  font-family: monospace;
  font-size: 11px;
  border-radius: 2px;
  border: 1px solid var(--soremo-border);
  background-color: var(--soremo-background);
  padding: 12px 4px;
  color: var(--soremo-light-gray);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const HintText = styled.p.attrs({ className: "bodytext-11" })`
  margin: 0;
  color: var(--soremo-light-gray);
`;

/** 今日から30日後の Date（プロジェクト完了日が無い場合のフォールバック） */
const fallbackExpiryDate = (): Date => {
  const d = new Date();
  d.setDate(d.getDate() + 30);
  return d;
};

/** Date → YYYY-MM-DD（BE へ送る形式） */
const toDateString = (d: Date): string => {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

// カレンダーを input 直下（static 配置）ではなく body 直下に描画する。
// モーダル本体は overflow: hidden のため、static 配置だとはみ出した部分が
// 隠れてしまう（オプション object は再レンダーで identity が変わらないよう固定）。
const CALENDAR_OPTIONS = { static: false };

interface ShareSettings {
  expiryEnabled: boolean;
  expiryDate: Date;
  membershipRequired: boolean;
}

/**
 * 共有リンク管理モーダル。
 *
 * - 共有トグル（先頭）: ONでリンク発行、OFFで共有解除
 * - 有効期限トグル（既定オン。プロジェクトの開発完了日があらかじめ設定される）
 * - アクセス制限トグル（既定オン = プロジェクトメンバーのみ。
 *   オフにするとサインインしていないユーザーもアクセス可能）
 * - 発行されたURLの表示とコピー（マイページのMCPと同デザイン）
 *
 * リンクは対象ごとに1本で、設定変更は同じURLのまま即時反映される。
 */
const ShareLinkModal: React.FC<ShareLinkModalProps> = ({
  projectId,
  target,
  defaultExpiryDate,
  onClose,
  onShareStateChanged,
}) => {
  const [initialized, setInitialized] = useState(false);
  const [shareEnabled, setShareEnabled] = useState(false);
  const [settings, setSettings] = useState<ShareSettings>({
    expiryEnabled: true,
    expiryDate: defaultExpiryDate
      ? new Date(`${defaultExpiryDate}T00:00:00`)
      : fallbackExpiryDate(),
    membershipRequired: true,
  });
  const [token, setToken] = useState<string | null>(null);
  const [isBusy, setIsBusy] = useState(false);

  const [fetchShareLink] = useLazyGetShareLinkQuery();
  const [createShareLink] = useCreateShareLinkMutation();
  const [deleteShareLink] = useDeleteShareLinkMutation();

  const { copy: copyToClipboard } = useCopyToClipboard({
    successMessage: "共有URLをコピーしました",
  });

  const targetParams =
    target.type === "file" ? { file_id: target.id } : { folder_id: target.id };

  // 既存リンクの取得（共有中なら設定を復元して表示）
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const result = await fetchShareLink({
          project_id: projectId,
          ...targetParams,
        }).unwrap();
        if (cancelled) return;
        const link = result.data;
        if (link) {
          setShareEnabled(true);
          setToken(link.token);
          setSettings((prev) => ({
            expiryEnabled: link.expires_at !== null,
            expiryDate: link.expires_at
              ? new Date(link.expires_at)
              : prev.expiryDate,
            membershipRequired: link.require_membership,
          }));
        }
      } catch {
        if (!cancelled) toast.error("共有リンクの取得に失敗しました");
      } finally {
        if (!cancelled) setInitialized(true);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
    // 対象が変わったときのみ再取得する
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, target.type, target.id]);

  // リンクの発行 / 設定更新（同じトークンのまま更新される）
  const issueLink = useCallback(
    async (next: ShareSettings): Promise<boolean> => {
      setIsBusy(true);
      try {
        const result = await createShareLink({
          project_id: projectId,
          ...targetParams,
          expires_at: next.expiryEnabled ? toDateString(next.expiryDate) : null,
          require_membership: next.membershipRequired,
        }).unwrap();
        setToken(result.data.token);
        return true;
      } catch (err) {
        const message =
          (err as { data?: { message?: string } })?.data?.message ||
          "共有リンクの発行に失敗しました";
        toast.error(message);
        return false;
      } finally {
        setIsBusy(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [projectId, target.type, target.id, createShareLink],
  );

  // 共有トグル: ON = 発行 / OFF = 解除
  const handleShareToggle = useCallback(async () => {
    if (isBusy || !initialized) return;
    if (!shareEnabled) {
      const ok = await issueLink(settings);
      if (ok) {
        setShareEnabled(true);
        onShareStateChanged?.(target, true);
      }
      return;
    }
    setIsBusy(true);
    try {
      await deleteShareLink({ project_id: projectId, ...targetParams }).unwrap();
      setShareEnabled(false);
      setToken(null);
      onShareStateChanged?.(target, false);
      toast.success("共有を解除しました");
    } catch {
      toast.error("共有の解除に失敗しました");
    } finally {
      setIsBusy(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isBusy,
    initialized,
    shareEnabled,
    settings,
    issueLink,
    deleteShareLink,
    projectId,
    onShareStateChanged,
  ]);

  // 設定変更（共有中は即時にリンクへ反映する）
  const applySettings = useCallback(
    (patch: Partial<ShareSettings>) => {
      setSettings((prev) => {
        const next = { ...prev, ...patch };
        if (shareEnabled) issueLink(next);
        return next;
      });
    },
    [shareEnabled, issueLink],
  );

  const shareUrl =
    shareEnabled && token
      ? `${window.location.origin}/share/${token}`
      : null;

  return (
    <Modal
      title="共有"
      titleSize="medium"
      size="default"
      description={
        target.type === "folder" ? `フォルダ「${target.name}」` : target.name
      }
      onClose={onClose}
    >
      <div className="u-w100 u-col u-gap24">
        {/* 共有 ON/OFF */}
        <div className="u-w100 u-col u-gap8">
          <Toggle value={shareEnabled} onClick={handleShareToggle}>
            <span className="bodytext-13">共有</span>
          </Toggle>
          <HintText>
            {shareEnabled
              ? "共有中です。オフにすると発行済みのリンクは無効になります"
              : "オンにすると共有リンクが発行されます"}
          </HintText>
        </div>

        {/* 共有中のみ設定とURLを表示する */}
        {shareEnabled && (
          <>
            {/* 有効期限 */}
            <div className="u-w100 u-col u-gap8">
              <Toggle
                value={settings.expiryEnabled}
                onClick={() =>
                  applySettings({ expiryEnabled: !settings.expiryEnabled })
                }
              >
                <span className="bodytext-13">有効期限</span>
              </Toggle>
              {settings.expiryEnabled ? (
                <DateInput
                  value={settings.expiryDate}
                  minDate={toDateString(new Date())}
                  width="240px"
                  options={CALENDAR_OPTIONS}
                  onChange={(e) => {
                    if (e.target.value instanceof Date) {
                      applySettings({ expiryDate: e.target.value });
                    }
                  }}
                />
              ) : (
                <HintText>期限なしでアクセスできます</HintText>
              )}
            </div>

            {/* アクセス制限 */}
            <div className="u-w100 u-col u-gap8">
              <Toggle
                value={settings.membershipRequired}
                onClick={() =>
                  applySettings({
                    membershipRequired: !settings.membershipRequired,
                  })
                }
              >
                <span className="bodytext-13">アクセス制限</span>
              </Toggle>
              <HintText>
                {settings.membershipRequired
                  ? "プロジェクトメンバーのみアクセスできます"
                  : "リンクを知っていれば、サインインしていないユーザーもアクセスできます"}
              </HintText>
            </div>

            {/* URL表示とコピー（MCPと同デザイン） */}
            <div className="u-w100 u-row u-gap4">
              <URLText>
                {shareUrl ?? (isBusy ? "リンクを発行中..." : "")}
              </URLText>
              <ToolTip
                description="共有URLをクリップボードにコピー"
                placement="top"
              >
                <IconButton
                  icon="content_copy"
                  size={16}
                  disabled={!shareUrl}
                  onClick={() => shareUrl && copyToClipboard(shareUrl)}
                />
              </ToolTip>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

export default ShareLinkModal;
